import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import ReportService from '@services/ReportService';
import { ClearIndicatorComponent as ClearIndicator } from '@/components/Reports/ClearIndicatorComponent';
import { buildReportSelectStyles } from '@/components/Reports/reportSelectStyles';
import './ETACostReport.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ProjectOption {
  value: number;
  label: string;
}

interface ETACostRawItem {
  Employee: string | null;
  AssignedTo?: string | null;
  Team: string | null;
  Milestone: string | null;
  CardType: string | null;
  CardName: string | null;
  PhaseName: string | null;
  LayerName: string | null;
  Estimated: string | null;
  CoreTaskId: number;
  Position: string | null;
  Rate: number;
}

interface ETACostRow {
  id: number;
  CoreTaskId: number;
  Milestone: string;
  CardType: string;
  CardName: string;
  PhaseName: string;
  LayerName: string;
  Assigned: string;
  Position: string;
  Rate: number;
  Estimated: number;
  TaskPrice: number;
}

type SortKey =
  | 'default'
  | 'project'
  | 'card_type'
  | 'card_name'
  | 'phase_name'
  | 'layer'
  | 'employee'
  | 'position'
  | 'rate'
  | 'estimated'
  | 'task_price';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function processResults(raw: ETACostRawItem[]): ETACostRow[]
{
  const groupMap = new Map<string, ETACostRawItem[]>();
  for (const item of raw)
  {
    const key = `${item.CoreTaskId}${item.LayerName ?? ''}`;
    if (!groupMap.has(key)) groupMap.set(key, []);
    groupMap.get(key)!.push(item);
  }

  const rows: ETACostRow[] = [];
  let idx = 1;
  for (const group of groupMap.values())
  {
    const first = group[0]!;
    const avgRate = group.reduce((sum, x) => sum + (x.Rate ?? 0), 0) / group.length;
    const estimated = first.Estimated ? parseFloat(first.Estimated) : 0;
    const assigned = group
      .map(x => x.AssignedTo ?? x.Employee ?? '')
      .filter(Boolean)
      .join(', ');
    const position = group.map(x => x.Position || 'Not Set').join(', ');

    rows.push({
      id: idx++,
      CoreTaskId: first.CoreTaskId,
      Milestone: first.Milestone ?? '',
      CardType: first.CardType ?? '',
      CardName: first.CardName ?? '',
      PhaseName: first.PhaseName ?? '',
      LayerName: first.LayerName ?? '',
      Assigned: assigned,
      Position: position,
      Rate: parseFloat(avgRate.toFixed(2)),
      Estimated: estimated,
      TaskPrice: avgRate * estimated,
    });
  }
  return rows;
}

function sortRows(rows: ETACostRow[], key: SortKey, dir: 'asc' | 'desc' | ''): ETACostRow[]
{
  if (key === 'default' || !dir)
  {
    return [...rows].sort((a, b) => a.id - b.id);
  }
  const cmp = (a: ETACostRow, b: ETACostRow): number =>
  {
    switch (key)
    {
      case 'project':
        return (a.Milestone || '').localeCompare(b.Milestone || '');
      case 'card_type':
        return (a.CardType || '').localeCompare(b.CardType || '');
      case 'card_name':
        return (a.CardName || '').localeCompare(b.CardName || '');
      case 'phase_name':
        return (a.PhaseName || '').localeCompare(b.PhaseName || '');
      case 'layer':
        return (a.LayerName || '').localeCompare(b.LayerName || '');
      case 'employee':
        return (a.Assigned || '').localeCompare(b.Assigned || '');
      case 'position':
        return (a.Position || '').localeCompare(b.Position || '');
      case 'rate':
        return a.Rate - b.Rate;
      case 'estimated':
        return a.Estimated - b.Estimated;
      case 'task_price':
        return a.TaskPrice - b.TaskPrice;
      default:
        return 0;
    }
  };
  return [...rows].sort((a, b) => (dir === 'asc' ? cmp(a, b) : cmp(b, a)));
}

// ─── Sort icon (same pattern as AttendanceReport) ─────────────────────────────

const SortIcon: React.FC<{ col: SortKey; active: SortKey; dir: 'asc' | 'desc' | '' }> = ({
  col,
  active,
  dir,
}) =>
{
  if (active !== col) return <i className="fa fa-sort eta-sort-icon" />;
  if (dir === 'asc') return <i className="fa fa-sort-asc eta-sort-icon" />;
  return <i className="fa fa-sort-desc eta-sort-icon" />;
};

// ─── Component ────────────────────────────────────────────────────────────────

const ETACostReport: React.FC = () =>
{
  const isDark = document.documentElement.classList.contains('dark');
  const selectStyles = buildReportSelectStyles(isDark);

  const [projectOptions, setProjectOptions] = useState<ProjectOption[]>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(null);

  const [allRows, setAllRows] = useState<ETACostRow[]>([]);
  const [displayRows, setDisplayRows] = useState<ETACostRow[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>('default');
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | ''>('');

  const [totalPrice, setTotalPrice] = useState(0);
  const [totalEstimated, setTotalEstimated] = useState(0);
  const [totalDays, setTotalDays] = useState(0);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('Please select at least a project to display results.');

  useEffect(() =>
  {
    document.title = 'Corgee';
  }, []);

  useEffect(() =>
  {
    ReportService.GetAllProject({ GroupByDetailId: 0, GroupByLayerId: 0, GroupByValueField: '' })
      .then((data: any) =>
      {
        const projects: ProjectOption[] = (data?.Projects || []).map((p: any) => ({
          value: p.ProjectID,
          label: p.Name,
        }));
        setProjectOptions(projects);
      })
      .catch(() => {});
  }, []);

  const applyFilters = useCallback(async () =>
  {
    if (!selectedProject)
    {
      setMessage('Please enter a valid filter');
      setDisplayRows([]);
      setAllRows([]);
      setTotalPrice(0);
      setTotalEstimated(0);
      setTotalDays(0);
      return;
    }

    setLoading(true);
    setMessage('');
    setDisplayRows([]);
    setAllRows([]);
    setSortKey('default');
    setSortDir('');

    try
    {
      const raw: ETACostRawItem[] = await ReportService.GetETACostReport({
        projectId: selectedProject.value,
      });

      if (!raw || raw.length === 0)
      {
        setMessage('No results found');
        setTotalPrice(0);
        setTotalEstimated(0);
        setTotalDays(0);
      }
      else
      {
        const rows = processResults(raw);
        const sumEstimated = rows.reduce((s, r) => s + r.Estimated, 0);
        const sumPrice = rows.reduce((s, r) => s + r.TaskPrice, 0);

        setAllRows(rows);
        setDisplayRows(rows);
        setTotalEstimated(parseFloat(sumEstimated.toFixed(2)));
        setTotalPrice(parseFloat(sumPrice.toFixed(2)));
        setTotalDays(parseFloat((sumEstimated / 8).toFixed(2)));
      }
    }
    catch
    {
      setMessage('Error loading data');
    }

    setLoading(false);
  }, [selectedProject]);

  const handleSortClick = useCallback(
    (col: SortKey) =>
    {
      let newDir: 'asc' | 'desc' | '';
      let newKey: SortKey;

      if (sortKey === col && sortDir === 'asc')
      {
        newDir = 'desc';
        newKey = col;
      }
      else if (sortKey === col && sortDir === 'desc')
      {
        newDir = '';
        newKey = 'default';
      }
      else
      {
        newDir = 'asc';
        newKey = col;
      }

      const sorted = sortRows(allRows, newKey, newDir).map((row, i) => ({ ...row, id: i + 1 }));
      setSortKey(newKey);
      setSortDir(newDir);
      setDisplayRows(sorted);
    },
    [sortKey, sortDir, allRows],
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  const th = (col: SortKey, label: string) => (
    <th onClick={() => handleSortClick(col)}>
      {label} <SortIcon col={col} active={sortKey} dir={sortDir} />
    </th>
  );

  return (
    <div id="ETACostReportPage">
      <h3 className="eta-title">ETA Cost Report</h3>
      <p className="eta-subtitle">
        This report contains information on the hours estimated by the users in their cards as well
        as their rate and position.
      </p>

      {/* Filters */}
      <div className="eta-filters">
        <div className="eta-filter-row">
          <div className="eta-filter-item">
            <label className="eta-label">Project(s):</label>
            <Select<ProjectOption, false>
              options={projectOptions}
              value={selectedProject}
              onChange={opt => setSelectedProject(opt)}
              isClearable
              isSearchable
              placeholder="Select..."
              styles={selectStyles}
              components={{ ClearIndicator }}
            />
          </div>
          <button className="eta-apply-btn" onClick={applyFilters} disabled={loading}>
            {loading ? 'Loading...' : 'Apply Filters'}
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="eta-summary">
        <div className="eta-summary-item">
          <span className="eta-summary-value">{currencyFormatter.format(totalPrice)}</span>
          <span className="eta-summary-label">Total Price</span>
        </div>
        <div className="eta-summary-item">
          <span className="eta-summary-value">{totalEstimated}</span>
          <span className="eta-summary-label">Time Estimated</span>
        </div>
        <div className="eta-summary-item">
          <span className="eta-summary-value">{totalDays}</span>
          <span className="eta-summary-label">ETA Days</span>
        </div>
      </div>

      {/* Table */}
      <div className="eta-table-wrapper">
        <div className="eta-table-scroll">
          <table className="eta-table">
            <thead>
              <tr>
                <th>No.</th>
                {th('project', 'Project')}
                {th('card_type', 'Card Type')}
                {th('card_name', 'Card Name')}
                {th('phase_name', 'Phase Name')}
                {th('layer', 'Layer')}
                {th('employee', 'Employee(s)')}
                {th('position', 'Position(s)')}
                {th('rate', 'Average Rate(s)')}
                {th('estimated', 'Estimated')}
                {th('task_price', 'Price per Task')}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={11} className="eta-no-data">
                    Loading...
                  </td>
                </tr>
              )}
              {!loading &&
                displayRows.map(row => (
                  <tr key={`${row.CoreTaskId}_${row.LayerName}_${row.id}`}>
                    <td>{row.id}</td>
                    <td title={row.Milestone}>{row.Milestone}</td>
                    <td title={row.CardType}>{row.CardType}</td>
                    <td title={row.CardName}>{row.CardName}</td>
                    <td title={row.PhaseName}>{row.PhaseName}</td>
                    <td title={row.LayerName}>{row.LayerName}</td>
                    <td title={row.Assigned}>{row.Assigned}</td>
                    <td title={row.Position}>{row.Position}</td>
                    <td>{currencyFormatter.format(row.Rate)}</td>
                    <td>{row.Estimated}</td>
                    <td>{currencyFormatter.format(row.TaskPrice)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {!loading && message && <div className="eta-no-data">{message}</div>}
    </div>
  );
};

export default ETACostReport;
